/**
 * SPA «Элеонора» — экраны аутентификации (задача 1) в едином визуальном слое
 * (задача 5), с постоянным мастхедом (задача 6) и восстановлением входа после
 * перезагрузки страницы (задача 7). Форма заявки (задача 3) живёт в
 * src/frontend/application.ts, экран «Администратор» (задача 23) — в
 * src/frontend/adminScreen.ts, экран «Организатор» (задача 24) — в
 * src/frontend/organizerScreen.ts, его экран «Заявка команды» (задача 43) — в
 * src/frontend/organizerTeamScreen.ts.
 * Стили — src/frontend/styles.css, разметка — хелперы src/frontend/dom.ts,
 * хранение сессии — src/frontend/session.ts, смена адреса и экрана —
 * src/frontend/router.ts (функция start() ниже — единственный источник выбора экрана).
 * Маршрутизация: ?token=... — подтверждение email, ?reset=... — смена пароля по ссылке
 * из письма (задача 28), #/register, #/login и #/forgot-password — экраны
 * аутентификации, #/application, #/organizer и #/admin — экраны ролей,
 * #/organizer/team?ownerUserId=… — заявка одной команды (задача 43, вложенный маршрут
 * раздела «Организатор»).
 */
import {
  ApiError,
  confirmEmail,
  confirmPasswordReset,
  loadApiConfig,
  login,
  register,
  requestPasswordReset,
} from './api';
import { showAdminScreen } from './adminScreen';
import { showApplicationScreen } from './application';
import { showOrganizerScreen } from './organizerScreen';
import { showOrganizerTeamScreen } from './organizerTeamScreen';
import {
  button,
  checkboxField,
  el,
  externalLink,
  field,
  link,
  messageBox,
  renderScreen,
} from './dom';
import {
  ADMIN_HREF,
  APPLICATION_HREF,
  FORGOT_PASSWORD_HREF,
  homeHref,
  LOGIN_HREF,
  ORGANIZER_HREF,
  REGISTER_HREF,
  organizerTeamHref,
  organizerTeamOwnerUserId,
  resetTokenFromSearch,
  roleScreenHrefs,
  setRouteRenderer,
  syncAddress,
} from './router';
import { loadSession, saveSession } from './session';
import { PERSONAL_DATA_CONSENT, PERSONAL_DATA_POLICY, TERMS_OF_SERVICE } from '../shared/consents';
import type { PublicUser } from '../shared/types';

/** Обёртка экрана аутентификации: центрированная колонка ограниченной ширины. */
function authScreen(children: (Node | null)[]): HTMLElement {
  return el('div', { className: 'auth', children });
}

/** Ссылки-переходы между экранами аутентификации: общий блок разметки. */
function authLinks(children: (Node | null)[]): HTMLElement {
  return el('div', { className: 'auth__links', children });
}

/**
 * Отрисовка экранов ролей: адрес экрана → функция отрисовки. Здесь же появляется
 * новый экран роли. Маршруты заданы в `src/frontend/router.ts` — меню аккаунта
 * (`src/frontend/accountMenu.ts`) строит и ссылки, и пункты по тем же адресам.
 */
const SCREEN_RENDERERS: Record<string, (user: PublicUser) => void> = {
  [APPLICATION_HREF]: showApplicationScreen,
  [ORGANIZER_HREF]: showOrganizerScreen,
  [ADMIN_HREF]: showAdminScreen,
};

/**
 * Показывает экран по адресу маршрута и приводит к нему адрес страницы.
 * Приведение обязательно: экран заявки покидают сменой хеша (пункт «Выйти», отказ
 * сессии 401), и хеш должен отличаться от целевого — иначе браузер не бросит
 * `hashchange` и переход «Выйти» → экран входа молча не произойдёт.
 */
function showRouteScreen(href: string, user: PublicUser): void {
  syncAddress(href);
  SCREEN_RENDERERS[href](user);
}

/**
 * Домашний экран вошедшего пользователя: экран старшей его роли (`homeHref`) —
 * у тренера форма заявки, у организатора раздел «Организатор». Показывается сразу
 * после входа (в адресе в этот момент ещё `#/login`) и при открытии адреса, который
 * роли не принадлежит (`start()`).
 */
function showHomeScreen(user: PublicUser): void {
  showRouteScreen(homeHref(user.roles), user);
}

/** Экран регистрации (сценарий 1 docs/spec.md). */
function showRegisterScreen(): void {
  const form = el('form', { className: 'card auth__card' });

  const firstName = el('input', { attrs: { name: 'firstName', autocomplete: 'given-name' } });
  const lastName = el('input', { attrs: { name: 'lastName', autocomplete: 'family-name' } });
  const organization = el('input', {
    attrs: { name: 'organizationName', autocomplete: 'organization' },
  });
  const email = el('input', {
    attrs: { type: 'email', name: 'email', inputmode: 'email', autocomplete: 'email' },
  });
  const password = el('input', {
    attrs: { type: 'password', name: 'password', autocomplete: 'new-password' },
  });
  const passwordConfirm = el('input', {
    attrs: { type: 'password', name: 'passwordConfirm', autocomplete: 'new-password' },
  });

  // Подписи полей и тексты сообщений не менялись с задачи 1 — меняется оформление.
  const fields = [
    field({ id: 'register-first-name', label: 'Имя', control: firstName }),
    field({ id: 'register-last-name', label: 'Фамилия', control: lastName }),
    field({ id: 'register-organization', label: 'Название организации', control: organization }),
    field({ id: 'register-email', label: 'Email', control: email }),
    field({ id: 'register-password', label: 'Пароль (минимум 8 символов)', control: password }),
    field({
      id: 'register-password-confirm',
      label: 'Подтверждение пароля',
      control: passwordConfirm,
    }),
  ];

  const message = messageBox();
  const submit = button({ text: 'Зарегистрироваться', variant: 'primary', type: 'submit' });

  // Юридические документы принимаются двумя отдельными чекбоксами (задача 18):
  // согласие на обработку ПДн нельзя объединять с другими подтверждениями
  // (ч. 1 ст. 9 152-ФЗ в редакции 156-ФЗ). Подписи ссылаются на страницы документов,
  // которые открываются в новой вкладке — введённые данные не теряются.
  // Тексты ссылок — в родительном и творительном падеже, поэтому берутся не из
  // названия документа (`consents.ts`), а заданы здесь; адреса и версии — оттуда.
  const terms = el('input', { attrs: { type: 'checkbox', name: 'termsAccepted' } });
  const consent = el('input', { attrs: { type: 'checkbox', name: 'consentAccepted' } });
  const termsField = checkboxField({
    id: 'register-terms',
    control: terms,
    label: ['Я принимаю условия ', externalLink('Пользовательского соглашения', TERMS_OF_SERVICE.path)],
  });
  const consentField = checkboxField({
    id: 'register-consent',
    control: consent,
    label: [
      'Я согласен с ',
      externalLink(
        'Соглашением об обработке и использовании персональных данных',
        PERSONAL_DATA_CONSENT.path,
      ),
    ],
  });

  /** Пока не отмечен хотя бы один документ, кнопка регистрации неактивна. */
  const updateSubmitState = (): void => {
    submit.disabled = !(terms.checked && consent.checked);
  };
  terms.addEventListener('change', updateSubmitState);
  consent.addEventListener('change', updateSubmitState);
  updateSubmitState();

  form.append(
    ...fields.map((item) => item.root),
    termsField.root,
    consentField.root,
    el('div', { className: 'form-actions', children: [submit] }),
    message.root,
    authLinks([
      externalLink(PERSONAL_DATA_POLICY.title, PERSONAL_DATA_POLICY.path),
      link('Уже зарегистрированы? Войти', LOGIN_HREF),
    ]),
  );

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (password.value !== passwordConfirm.value) {
      message.show('Пароли не совпадают', 'error');
      return;
    }
    try {
      const result = await register({
        firstName: firstName.value.trim(),
        lastName: lastName.value.trim(),
        organizationName: organization.value.trim(),
        email: email.value.trim(),
        password: password.value,
        // Версии редакций — из общего списка документов: сервер отклонит регистрацию,
        // если версия устарела (например, вкладка была открыта давно).
        termsAccepted: terms.checked,
        termsVersion: TERMS_OF_SERVICE.version,
        consentAccepted: consent.checked,
        consentVersion: PERSONAL_DATA_CONSENT.version,
      });
      message.show(result.message, 'success');
      form.reset();
      // reset() не бросает change: состояние кнопки пересчитываем явно.
      updateSubmitState();
    } catch (err) {
      message.show(err instanceof ApiError ? err.message : 'Неизвестная ошибка', 'error');
    }
  });

  renderScreen('Регистрация', authScreen([form]));
}

/** Экран входа (сценарий 2 docs/spec.md). */
function showLoginScreen(): void {
  const form = el('form', { className: 'card auth__card' });

  const email = el('input', {
    attrs: { type: 'email', name: 'email', inputmode: 'email', autocomplete: 'username' },
  });
  const password = el('input', {
    attrs: { type: 'password', name: 'password', autocomplete: 'current-password' },
  });

  const message = messageBox();
  const submit = button({ text: 'Войти', variant: 'primary', type: 'submit' });

  form.append(
    field({ id: 'login-email', label: 'Email', control: email }).root,
    field({ id: 'login-password', label: 'Пароль', control: password }).root,
    el('div', { className: 'form-actions', children: [submit] }),
    message.root,
    authLinks([
      link('Нет аккаунта? Зарегистрироваться', REGISTER_HREF),
      // Восстановление пароля (задача 28): запрос ссылки письмом на экране #/forgot-password.
      link('Забыли пароль?', FORGOT_PASSWORD_HREF),
    ]),
  );

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    try {
      const result = await login(email.value.trim(), password.value);
      // Сессия переживает перезагрузку страницы: токен и профиль лежат в localStorage.
      saveSession(result.token, result.user);
      showHomeScreen(result.user);
    } catch (err) {
      message.show(err instanceof ApiError ? err.message : 'Неизвестная ошибка', 'error');
    }
  });

  renderScreen('Вход', authScreen([form]));
}

/** Экран подтверждения email: открывается по ссылке из письма (?token=...). */
function showConfirmScreen(token: string): void {
  const message = messageBox();
  message.show('Подтверждаем email…', 'info');

  const screen = authScreen([message.root]);
  renderScreen('Подтверждение email', screen);

  /**
   * Завершает подтверждение: добавляет ссылку перехода и приводит адрес страницы ко
   * входу. Токен одноразовый — после ответа сервера он из адреса убирается, иначе
   * он остаётся приоритетным для `start()` и ссылка «Перейти ко входу» снова
   * показывала бы этот же экран вместо экрана входа.
   */
  const finish = (text: string): void => {
    syncAddress(LOGIN_HREF);
    screen.append(authLinks([link(text, LOGIN_HREF)]));
  };

  confirmEmail(token)
    .then((result) => {
      message.show(result.message, 'success');
      finish('Перейти ко входу');
    })
    .catch((err: unknown) => {
      message.show(err instanceof ApiError ? err.message : 'Неизвестная ошибка', 'error');
      finish('Вернуться ко входу');
    });
}

/**
 * Экран «Забыли пароль?» (#/forgot-password, задача 28): запрос ссылки восстановления.
 * После отправки показывается текст сервера — он одинаков для известного и неизвестного
 * адреса, поэтому экран не раскрывает, кто зарегистрирован в сервисе.
 */
function showForgotPasswordScreen(): void {
  const form = el('form', { className: 'card auth__card' });

  const email = el('input', {
    attrs: { type: 'email', name: 'email', inputmode: 'email', autocomplete: 'email' },
  });

  const message = messageBox();
  const submit = button({ text: 'Отправить ссылку', variant: 'primary', type: 'submit' });

  form.append(
    field({ id: 'forgot-email', label: 'Email', control: email }).root,
    el('div', { className: 'form-actions', children: [submit] }),
    message.root,
    authLinks([link('Вернуться ко входу', LOGIN_HREF)]),
  );

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    try {
      const result = await requestPasswordReset(email.value.trim());
      message.show(result.message, 'success');
    } catch (err) {
      message.show(err instanceof ApiError ? err.message : 'Неизвестная ошибка', 'error');
    }
  });

  renderScreen('Восстановление пароля', authScreen([form]));
}

/**
 * Экран смены пароля по ссылке из письма (?reset=…, задача 28). Токен одноразовый:
 * после успешной смены адрес приводится ко входу (`syncAddress`), а форма заменяется
 * сообщением — повторная отправка того же токена всё равно получила бы отказ.
 * Совпадение паролей проверяется на клиенте, как при регистрации; длину проверяет
 * сервер теми же правилами.
 */
function showResetPasswordScreen(token: string): void {
  const form = el('form', { className: 'card auth__card' });

  const password = el('input', {
    attrs: { type: 'password', name: 'password', autocomplete: 'new-password' },
  });
  const passwordConfirm = el('input', {
    attrs: { type: 'password', name: 'passwordConfirm', autocomplete: 'new-password' },
  });

  const message = messageBox();
  const submit = button({ text: 'Сохранить пароль', variant: 'primary', type: 'submit' });

  form.append(
    field({
      id: 'reset-password',
      label: 'Новый пароль (минимум 8 символов)',
      control: password,
    }).root,
    field({ id: 'reset-password-confirm', label: 'Повторите пароль', control: passwordConfirm }).root,
    el('div', { className: 'form-actions', children: [submit] }),
    message.root,
    authLinks([link('Запросить новую ссылку', FORGOT_PASSWORD_HREF)]),
  );

  const screen = authScreen([form]);
  renderScreen('Восстановление пароля', screen);

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (password.value !== passwordConfirm.value) {
      message.show('Пароли не совпадают', 'error');
      return;
    }
    try {
      const result = await confirmPasswordReset(token, password.value);
      // Токен и параметр в адресе одноразовые: убираем `?reset=…` из адреса, иначе
      // `start()` снова показал бы этот экран вместо экрана входа.
      syncAddress(LOGIN_HREF);
      screen.replaceChildren(message.root, authLinks([link('Перейти ко входу', LOGIN_HREF)]));
      message.show(result.message, 'success');
    } catch (err) {
      // Ошибка ссылки или пароля: форма остаётся, чтобы можно было исправить пароль,
      // а ссылку на новый запрос экран показывает всегда (authLinks выше).
      message.show(err instanceof ApiError ? err.message : 'Неизвестная ошибка', 'error');
    }
  });
}

/**
 * Запускает SPA: смена пароля по ссылке из письма (?reset=…), подтверждение email
 * (?token=…), восстановление сессии (вошедшему — экран его роли или запрошенный экран
 * роли) или экран по маршруту (#/register, #/login, #/forgot-password). Единственный
 * источник выбора экрана: вызывается на старте и по `hashchange`, а также напрямую из
 * `navigate()` (`src/frontend/router.ts`).
 */
function start(): void {
  // Одноразовые ссылки из писем важнее маршрута и восстановленной сессии: сначала смена
  // пароля (?reset=…), затем подтверждение email (?token=…) — задача 28.
  const resetToken = resetTokenFromSearch(window.location.search);
  if (resetToken) {
    showResetPasswordScreen(resetToken);
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (token) {
    showConfirmScreen(token);
    return;
  }

  // Восстановление входа после перезагрузки: сессия приоритетнее маршрута — вошедшему
  // пользователю нужен его экран, а не экран входа.
  const session = loadSession();
  if (session) {
    const requested = window.location.hash;
    // Заявка команды (задача 43) — вложенный маршрут раздела «Организатор»: адрес несёт
    // адрес команды, поэтому его разбирает отдельное правило, а не список домашних адресов
    // ролей. Права проверяет API: здесь решается только, какой экран нарисовать, — чужой
    // роли адрес команды покажет домашний экран её роли. Адрес приводится к каноническому
    // виду: лишние параметры ссылки на экран не влияют.
    const teamOwnerUserId = organizerTeamOwnerUserId(requested);
    if (teamOwnerUserId && session.user.roles.includes('organizer')) {
      syncAddress(organizerTeamHref(teamOwnerUserId));
      showOrganizerTeamScreen(teamOwnerUserId, session.user);
      return;
    }
    // Адрес экрана роли открывается напрямую (ссылки меню аккаунта): адрес своей роли —
    // как есть, чужой или неизвестный — домашний экран роли, чтобы вместо пустой
    // страницы показать доступный раздел. Права проверяет API, а не маршрут.
    const href = roleScreenHrefs(session.user.roles).includes(requested)
      ? requested
      : homeHref(session.user.roles);
    showRouteScreen(href, session.user);
    return;
  }

  // Адрес приводится к показанному экрану: «Выйти» и ссылки между экранами аутентификации
  // переходят сменой хеша, а он не должен совпадать с целевым (иначе ни `hashchange`, ни
  // сама ссылка не переключат экран).
  const route = window.location.hash.replace(/^#\/?/, '');
  if (route === 'register') {
    syncAddress(REGISTER_HREF);
    showRegisterScreen();
    return;
  }

  if (route === 'forgot-password') {
    syncAddress(FORGOT_PASSWORD_HREF);
    showForgotPasswordScreen();
    return;
  }

  syncAddress(LOGIN_HREF);
  showLoginScreen();
}

// Отрисовку маршрута знает только `start()`: `navigate()` вызывает её через реестр.
setRouteRenderer(start);
window.addEventListener('hashchange', start);

// Загружаем конфигурацию API (URL бэкенда, сгенерированный CI/CD), затем запускаем SPA.
loadApiConfig().finally(start);

